// Catalogue of the creations known to a client or a server: official, local (the player's drafts)
// and library copies. Indexed by id and by type, lists recomputed only after a change.
import type { CreationKind, CreationOf, Fact, PlanContent, Provenance } from '@gruvetto/contracts';
import { LIBRARY, PLANS, TRACKS, MODES, UNLOCKS } from '@gruvetto/core';
import { OFFICIAL_FRAMES, OFFICIAL_HELMETS, OFFICIAL_WHEELS } from './kinds/cosmetic.js';
import { OFFICIAL_THEMES } from './kinds/theme.js';
import { OFFICIAL_WORLDS } from './world/presets.js';
import type { KindContext, KindDef } from './kind.js';
import { OFFICIAL_JERSEYS } from './kinds/cosmetic.js';
import { KINDS } from './registry.js';

export type Source = 'builtin' | 'local' | 'library';
export interface EntryOf<K extends CreationKind> {
  id: string; version: number; source: Source; creation: CreationOf<K>;
  author: { id: string; name: string } | null;
  /** Player level required (official content to unlock). */
  level?: number;
  remixOf?: { id: string; version: number } | null;
  /** Corresponding published copy (local draft already published, or library copy). */
  remote?: { id: string; version: number } | null;
  provenance?: Provenance | null;
  updatedAt: number;
}

/** Catalogue entry; without a parameter, a union discriminated on creation.kind (a switch on the type narrows the content). */
export type Entry<K extends CreationKind = CreationKind> = K extends CreationKind ? EntryOf<K> : never;
export type AnyEntry = Entry;

export const GRUVETTO = { id: 'gruvetto', name: 'Gruvetto' };

/** Adaptation of the official plans: recovery if tired, lighter load if workouts are missed. */
const DEFAULT_ADAPT: PlanContent['adapt'] = [{ when: 'fatigue', tsbBelow: -20, workout: 'recup30' }, { when: 'failed', complianceBelow: 70, easier: 0.05 }, { when: 'missed', days: 6, then: 'notice' }];

/** Catalogue id of an official cosmetic ("frame-or") and the way back to its slot and pattern. */
export const officialCosmeticId = (slot: string, id: string) => `${slot}-${id}`;
export function officialCosmeticOf(entryId: string): { slot: 'jersey' | 'frame' | 'helmet' | 'wheels'; id: string } | null {
  const m = /^(jersey|frame|helmet|wheels)-(.+)$/.exec(entryId);
  return m ? { slot: m[1] as 'jersey', id: m[2] } : null;
}

/** Official content in the creations format: it proves the format is enough to describe the whole game. */
export function officialEntries(): AnyEntry[] {
  const e = <K extends CreationKind>(id: string, creation: CreationOf<K>, level?: number) => ({ id, version: 1, source: 'builtin', creation, author: GRUVETTO, level, updatedAt: 0 }) as AnyEntry;
  const lookLevel = (slot: 'pattern' | 'frame' | 'helmet', id: string) => UNLOCKS.find(u => u.kind === 'look' && u.slot === slot && u.id === id)?.level;
  const wearable = (slot: 'jersey' | 'frame' | 'helmet' | 'wheels', table: Record<string, { name: string; design: unknown }>, unlock?: 'pattern' | 'frame' | 'helmet') =>
    Object.entries(table).map(([id, j]) => e(officialCosmeticId(slot, id), { kind: 'cosmetic', name: j.name, desc: '', tags: [], content: { slot, design: j.design } } as CreationOf<'cosmetic'>, unlock ? lookLevel(unlock, id) : undefined));
  return [
    ...LIBRARY.map(w => e(w.id, { kind: 'workout', name: w.name, desc: w.desc || '', tags: [w.cat], content: { cat: w.cat, ...(w.test ? { test: w.test } : {}), segs: w.segs } } as CreationOf<'workout'>)),
    ...PLANS.map(p => e(p.id, { kind: 'plan', name: p.name, desc: p.desc, tags: [], content: { goal: p.desc, workouts: [], weeks: p.weeks.map(ids => ({ sessions: ids.map(workout => ({ workout })) })), adapt: DEFAULT_ADAPT } })),
    ...Object.entries(TRACKS).map(([id, t]) => e(id, { kind: 'track', name: t.name, desc: t.desc || '', tags: [], content: { points: t.pts as [number, number, number][], ...(t.open ? { open: true } : {}), ...(t.alt0 != null ? { altitude: t.alt0 } : {}), ...(OFFICIAL_WORLDS[id] ? { world: OFFICIAL_WORLDS[id] } : {}) } }, t.level)),
    ...Object.entries(MODES).filter(([, m]) => !m.hidden).map(([id, m]) => e(id, { kind: 'mode', name: m.name, desc: m.desc, tags: [], content: { icon: m.icon, rules: m.rules } })),
    ...Object.entries(OFFICIAL_THEMES).map(([id, t]) => e(id, { kind: 'theme', name: t.name, desc: t.desc, tags: [], content: t.content }, t.level ?? UNLOCKS.find(u => u.kind === 'mood' && u.id === id)?.level)),
    ...wearable('jersey', OFFICIAL_JERSEYS, 'pattern'), ...wearable('frame', OFFICIAL_FRAMES, 'frame'), ...wearable('helmet', OFFICIAL_HELMETS, 'helmet'), ...wearable('wheels', OFFICIAL_WHEELS),
  ];
}

export class Catalog {
  private byId = new Map<string, AnyEntry>();
  private lists = new Map<CreationKind, AnyEntry[]>();
  private listeners = new Set<() => void>();
  private factCache = new Map<string, { key: string; facts: Fact[] }>();
  /** Context of the checks and key figures (player's FTP, workout resolution). */
  readonly ctx: KindContext;

  constructor(ftp: () => number) {
    const get = (id: string) => this.get(id, 'workout')?.creation ?? null;
    this.ctx = { get ftp() { return ftp(); }, findWorkout: get };
  }

  put(...entries: AnyEntry[]) {
    for (const e of entries) { this.byId.set(e.id, e); this.lists.delete(e.creation.kind); this.factCache.delete(e.id); }
    this.changed();
  }
  remove(id: string) {
    const e = this.byId.get(id);
    if (!e) return;
    this.byId.delete(id); this.lists.delete(e.creation.kind); this.factCache.delete(id);
    this.changed();
  }
  /** Removes every entry of a source (reloading the library copies, for example). */
  clear(source: Source) { for (const [id, e] of this.byId) if (e.source === source) { this.byId.delete(id); this.lists.delete(e.creation.kind); this.factCache.delete(id); } this.changed(); }

  get<K extends CreationKind>(id: string, kind?: K): Entry<K> | null {
    const e = this.byId.get(id);
    return e && (!kind || e.creation.kind === kind) ? (e as unknown as Entry<K>) : null;
  }
  /** Entries of a type: official ones in their order, then the most recent. */
  list<K extends CreationKind>(kind: K): Entry<K>[] {
    let l = this.lists.get(kind);
    if (!l) {
      const all = [...this.byId.values()].filter(e => e.creation.kind === kind);
      l = [...all.filter(e => e.source === 'builtin'), ...all.filter(e => e.source !== 'builtin').sort((a, b) => b.updatedAt - a.updatedAt)];
      this.lists.set(kind, l);
    }
    return l as unknown as Entry<K>[];
  }
  /** Cached key figures (recomputed if the FTP changes). */
  facts(e: AnyEntry): Fact[] {
    const key = `${e.version}@${this.ctx.ftp}`, hit = this.factCache.get(e.id);
    if (hit?.key === key) return hit.facts;
    let facts: Fact[];
    try { facts = (KINDS[e.creation.kind] as KindDef).facts(e.creation, this.ctx); } catch { facts = []; } // tolerates an inconsistent draft
    this.factCache.set(e.id, { key, facts });
    return facts;
  }
  onChange(fn: () => void) { this.listeners.add(fn); return () => { this.listeners.delete(fn); }; }
  private changed() { for (const fn of this.listeners) fn(); }
}
